<script setup lang="ts">
import { ref, useTemplateRef, watch } from "vue";
import { UiAside, UiText, UiIconButton, UiStandardButton, UiDialog } from "@shopexperts/shared";
import type { Redirect } from "@shopexperts/shared";
import { useRedirects } from "../composables/useRedirects";
import { useCreateRedirect } from "../composables/useCreateRedirect";
import { useUpdateRedirect } from "../composables/useUpdateRedirect";
import { useDeleteRedirect } from "../composables/useDeleteRedirect";
import RedirectCard from "../components/RedirectCard.vue";
import RedirectForm from "../components/RedirectForm.vue";

const { redirects, isLoading, error } = useRedirects();
const { mutateAsync: createRedirect } = useCreateRedirect();
const { mutateAsync: updateRedirect, isPending: isUpdating } = useUpdateRedirect();
const { mutateAsync: deleteRedirect, isPending: isDeleting } = useDeleteRedirect();

const asideOpen = ref(false);
const editingRedirect = ref<Redirect | null>(null);
const formRef = useTemplateRef('formRef');

// Delete confirmation state
const deleteConfirmOpen = ref(false);
const redirectToDelete = ref<Redirect | null>(null);

// Toggle active confirmation state
const toggleActiveConfirmOpen = ref(false);
const redirectToToggle = ref<Redirect | null>(null);
const switchResetNonce = ref<Record<number, number>>({});
const didConfirmToggleActive = ref(false);

function bumpSwitchResetNonce(id: number) {
  switchResetNonce.value = {
    ...switchResetNonce.value,
    [id]: (switchResetNonce.value[id] ?? 0) + 1,
  };
}

function onAddClick() {
  editingRedirect.value = null;
  formRef.value?.resetForm();
  asideOpen.value = true;
}

function onEditClick(redirect: Redirect) {
  editingRedirect.value = redirect;
  asideOpen.value = true;
}

function onDeleteClick(redirect: Redirect) {
  redirectToDelete.value = redirect;
  deleteConfirmOpen.value = true;
}

function onToggleActiveClick(redirect: Redirect) {
  redirectToToggle.value = redirect;
  didConfirmToggleActive.value = false;
  toggleActiveConfirmOpen.value = true;
}

// Reset the switch UI on any dialog close (X, overlay, ESC, secondary action),
// except when we closed due to a successful confirm.
watch(toggleActiveConfirmOpen, (isOpen, wasOpen) => {
  if (!wasOpen || isOpen) return;

  if (!didConfirmToggleActive.value && redirectToToggle.value) {
    bumpSwitchResetNonce(redirectToToggle.value.id);
  }

  redirectToToggle.value = null;
  didConfirmToggleActive.value = false;
});

async function confirmDelete() {
  if (!redirectToDelete.value) return;
  
  try {
    await deleteRedirect(redirectToDelete.value.id);
    deleteConfirmOpen.value = false;
    redirectToDelete.value = null;
  } catch (error) {
    console.error('Error deleting redirect:', error);
  }
}

function cancelDelete() {
  deleteConfirmOpen.value = false;
  redirectToDelete.value = null;
}

async function confirmToggleActive() {
  if (!redirectToToggle.value) return;
  
  try {
    await updateRedirect({
      id: redirectToToggle.value.id,
      data: {
        is_active: !redirectToToggle.value.is_active,
      },
    });
    didConfirmToggleActive.value = true;
    toggleActiveConfirmOpen.value = false;
  } catch (error) {
    console.error('Error toggling redirect active state:', error);
  }
}

function cancelToggleActive() {
  toggleActiveConfirmOpen.value = false;
}

async function onFormSubmit(data: { 
  source_path: string; 
  target_path: string; 
  status_code: number; 
  is_active: boolean;
  expires_at: string | null;
}) {
  try {
    if (editingRedirect.value) {
      await updateRedirect({
        id: editingRedirect.value.id,
        data: {
          source_path: data.source_path,
          target_path: data.target_path,
          status_code: data.status_code,
          is_active: data.is_active,
          expires_at: data.expires_at,
        },
      });
    } else {
      await createRedirect({
        source_path: data.source_path,
        target_path: data.target_path,
        status_code: data.status_code,
        is_active: data.is_active,
        expires_at: data.expires_at,
      });
    }
    formRef.value?.resetForm();
    asideOpen.value = false;
    editingRedirect.value = null;
  } catch (error) {
    console.error('Error submitting redirect form:', error);
  }
}

function onFormCancel() {
  formRef.value?.resetForm();
  asideOpen.value = false;
  editingRedirect.value = null;
}
</script>

<template>
  <main class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-3xl font-normal">Redirects</h1>
        <p class="mt-1 text-gray-600">Manage URL redirects for the website</p>
      </div>
      <UiStandardButton 
        variant="primary" 
        label="Add Redirect" 
        icon="plus-square"
        @click="onAddClick" 
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-8">
      <UiText variant="p" as="p" text="Loading redirects..." />
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8 text-red-500">
      <UiText variant="p" as="p" :text="`Failed to load redirects: ${error.message}`" />
    </div>

    <!-- Empty State -->
    <div v-else-if="redirects.length === 0" class="text-center py-12">
      <div class="flex flex-col items-center gap-4">
        <div>
          <UiText variant="h6" as="h6" text="No redirects found" class="font-semibold" />
          <UiText variant="p-small" as="p" text="Create your first redirect to get started" class="text-gray-600 mt-1" />
        </div>
        <UiStandardButton 
          variant="primary" 
          label="Add Redirect" 
          icon="plus-square"
          @click="onAddClick" 
        />
      </div>
    </div>

    <!-- Redirect List -->
    <div v-else class="space-y-4">
      <RedirectCard
        v-for="redirect in redirects"
        :key="`${redirect.id}-${switchResetNonce[redirect.id] ?? 0}`"
        :redirect="redirect"
        @edit="onEditClick(redirect)"
        @delete="onDeleteClick(redirect)"
        @toggle-active="onToggleActiveClick(redirect)"
      />
    </div>

    <!-- Add/Edit Aside -->
    <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
      <div class="px-4 py-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <div class="flex items-center justify-between">
          <UiText as="h3" :text="editingRedirect ? 'Edit Redirect' : 'Add Redirect'" />
          <UiIconButton icon="close" @click="onFormCancel" />
        </div>
        <UiText 
          as="p" 
          class="text-gray-600 mb-4" 
          :text="editingRedirect ? 'Update the redirect details below.' : 'Create a new URL redirect.'" 
        />
        
        <RedirectForm 
          ref="formRef"
          :editing="editingRedirect"
          @submit="onFormSubmit"
          @cancel="onFormCancel"
        />
      </div>
    </UiAside>

    <!-- Delete Confirmation Dialog -->
    <UiDialog
      v-model="deleteConfirmOpen"
      title="Delete Redirect"
      :text="`Are you sure you want to delete the redirect from '${redirectToDelete?.source_path}' to '${redirectToDelete?.target_path}'? This action cannot be undone.`"
      primary-action-label="Delete"
      secondary-action-label="Cancel"
      :primary-action-loading="isDeleting"
      @primary-action="confirmDelete"
      @secondary-action="cancelDelete"
    />

    <!-- Toggle Active Confirmation Dialog -->
    <UiDialog
      v-model="toggleActiveConfirmOpen"
      :title="redirectToToggle?.is_active ? 'Disable Redirect' : 'Enable Redirect'"
      :text="redirectToToggle?.is_active 
        ? `Are you sure you want to disable the redirect from '${redirectToToggle?.source_path}' to '${redirectToToggle?.target_path}'? The redirect will no longer be active.`
        : `Are you sure you want to enable the redirect from '${redirectToToggle?.source_path}' to '${redirectToToggle?.target_path}'? The redirect will become active.`"
      :primary-action-label="redirectToToggle?.is_active ? 'Disable' : 'Enable'"
      secondary-action-label="Cancel"
      :primary-action-loading="isUpdating"
      @primary-action="confirmToggleActive"
      @secondary-action="cancelToggleActive"
    />
  </main>
</template>

